
import { InputData } from '@/types/global';
import Vue from 'vue';
import { TransformedFmContact } from '~/transforms/transformModules';

export default Vue.extend({
  props: {
    data: {
      type: Object as () => TransformedFmContact['data'],
      required: true,
    },
  },

  data() {
    return {
      formSent: false,
      formError: '',
      formData: [] as Array<InputData>,
      formValid: false,
      postData: [] as Record<string, any>,
    };
  },

  mounted() {
    // Log Component
    this.$root.$on('addFile', this.addFileToForm);
    this.$root.$on('removeFile', this.removeFileFromForm);
    this.postData.files = [];

    // Log FormView Event
    if (process.client) {
      // @ts-ignore
      let dataLayer = window.dataLayer || [];
      dataLayer.push({
        event: 'formView',
        eventModel: { form_id: this.data.id, form_destination: window.location.href },
      });
    }
  },

  methods: {
    addFileToForm(file: any) {
      if (!this.postData.files) this.postData.files = [];

      // If file type is not an image, prop dataURL is not present.
      // In this case, we need to convert the file to a dataURL.
      if (!file.dataURL) {
        const reader = new FileReader();

        let receivedFile: string | ArrayBuffer | null = null;

        reader.readAsDataURL(file);
        reader.onload = function (event) {
          receivedFile = event.target?.result || null; // event.target.result contains base64 encoded image
        };
        reader.onloadend = () => receivedFile && this.postData.files.push({ label: file.name, value: receivedFile });
      }
      // File is of type image and property dataURL is present.
      // dataURL = BASE64 encoded Image (jpg/png).
      else {
        this.postData.files.push({
          label: file.name,
          value: file.dataURL,
        });
      }
    },

    removeFileFromForm(file: any) {
      this.postData.files = this.postData.files.filter((f: any) => f.label !== file.name);
    },

    async submitForm() {
      if (!process.env.STORYBOOK_ENABLED) {
        const customValidateEvent = new CustomEvent('validateForm', {
          bubbles: true,
        });
        this.$el.dispatchEvent(customValidateEvent);
        const form = this.$el.querySelector<HTMLFormElement>('form');
        const elements = form?.querySelectorAll('.c-input:not(.c-input-radio)');
        this.formValid = true;
        elements?.forEach((element: Element) => {
          const htmlElement = element as HTMLFormElement;
          if (htmlElement.dataset['valid'] !== 'true') {
            this.formValid = false;
          }
        });

        if (this.formValid) {
          const form = this.$el.querySelector('form') as HTMLFormElement;
          const elements = form.querySelectorAll('.c-input');
          elements.forEach((element: Element) => {
            const htmlElement = element as HTMLFormElement;
            const name = htmlElement.getAttribute('name') as string;
            if (htmlElement.classList.contains('c-input-textfield')) {
              let input = element.querySelector('input[type=text]') as HTMLInputElement;
              let label = element.querySelector('.c-input-textfield-label') as HTMLElement;
              this.formData.push({
                name: input.name,
                label: label.innerText,
                value: input.value,
              });
            }
            if (htmlElement.classList.contains('c-input-textarea')) {
              let input = element.querySelector('textarea') as HTMLTextAreaElement;
              let label = element.querySelector('.c-input-textarea-label') as HTMLElement;
              this.formData.push({
                name: input.name,
                label: label.innerText,
                value: input.value,
              });
            }
            if (htmlElement.classList.contains('c-input-checkbox')) {
              let input = element.querySelector('input[type=checkbox]') as HTMLInputElement;
              let label = element.querySelector('.c-input-checkbox-label') as HTMLElement;
              this.formData.push({
                name: input.name,
                label: label.innerText,
                value: input.value,
              });
            }
            if (htmlElement.classList.contains('c-input-select')) {
              const label = element.querySelector<HTMLElement>('.c-input-select-label');
              const selectedTags = element.querySelectorAll<HTMLElement>('.multiselect__tag > span');
              if (label && selectedTags.length > 0) {
                const name = label.getAttribute('for') || '';
                const value = Array.from(selectedTags).map(tag => tag.innerText).join(', ');
                this.formData.push({
                  name,
                  label: label.innerText,
                  value,
                });
              }
            }
            if (htmlElement.classList.contains('c-radio-group')) {
              let radios = element.querySelectorAll('input[type=radio]');
              let label = element.querySelector('.c-radio-group-label') as HTMLElement;
              const options = Array.from(radios);
              const selectedOption = options.filter(option => (option as HTMLInputElement).checked)[0];
              if (selectedOption) {
                const value = (selectedOption as HTMLInputElement).value;
                this.formData.push({
                  name: (selectedOption as HTMLInputElement).name,
                  label: label.innerText,
                  value: value,
                });
              }
            }
          });

          if (this.formData.length) {
            const { postData } = this;
            this.formData.forEach(input => {
              postData[input.name] = {
                label: input.label,
                value: input.value,
              };
            });

            // Add Receiver to PostData
            postData.receiver = { value: this.data.contactAddress };

            // Add Language to PostData
            const langCode = this.$route.path.split('/')[1] || 'de-de';
            postData.language = langCode.split('-')[1];

            const apiUrl =
              process.env.ENVIRONMENT === 'production'
                ? 'https://api.geigergruppe.com/send-mail'
                : process.env.ENVIRONMENT === 'local'
                ? 'http://localhost:4000/send-mail'
                : 'https://api.staging-5em2ouy-jvm3basdixqzm.de-2.platformsh.site/send-mail';

            try {
              const result = await this.$axios.post(apiUrl, { ...postData });

              // Check for specific success message.
              // Endpoint returns a fake success message if honeypot rule matched.
              // If the trap did *snaaap!*, the endpoint returns "Email was sent." instead of "Email has been sent."

              // Real Success
              if (result.data.success.status === 200 && result.data.success.message === 'Email has been sent.') {
                // Add query param for formSubmit tracking via gtm and other trackers.
                this.$router.push({ query: { ...this.$route.query, formSent: '1' } });
                this.formSent = true;
                if (process.client) {
                  // @ts-ignore
                  let dataLayer = window.dataLayer || [];
                  dataLayer.push({
                    event: 'formSubmit',
                    eventModel: { form_id: this.data.id, form_destination: window.location.href },
                  });
                }
              }

              // Fake Success
              if (result.data.success.status === 200 && result.data.success.message === 'Email was sent.') {
                // Add fake query param to prevent form submit tracking events.
                this.$router.push({ query: { ...this.$route.query, success: '1' } });
                this.formSent = true;
              }

              if (result.data.error) {
                this.formError = result.data.error.message || `Error: ${result.data.error.code}`;
              }
            } catch (error: any) {
              this.formError = `Fehler beim Senden: ${error.message}`;
            }
          }
        }
      }
    },
    formWatcher(e: any) {
      const customValidateEvent = new CustomEvent('validateField', {
        bubbles: true,
        detail: {
          element: e.target,
        },
      });
      this.$el.dispatchEvent(customValidateEvent);

      const form = this.$el.querySelector<HTMLFormElement>('form');
      const elements = form?.querySelectorAll<HTMLElement>('.c-input:not(.c-input-radio)');

      // Reset to true and check all fields
      this.formValid = true;

      if (elements) {
        elements.forEach(element => {
          if (element.dataset['valid'] !== 'true') {
            this.formValid = false;
          }
        });
      }
    },
  },
});
